import {
  AvailabilityDataList,
  ChannelDataList,
  DayDataList,
  DayDataTypes,
  HotelDataList,
  RoomTypeCapacityDataList,
  RoomTypeRateDataList
} from 'src/api'
import {
  CapacityRow,
  ChannelRow,
  RateRow,
  RatesDataGridRow,
  RowType
} from './RatesDataGridContext.types'

const dateListToMap = <T extends { date: string }>(dateList: T[]) =>
  dateList.reduce<Record<string, T>>((dateMap, dateData) => {
    dateMap[dateData.date] = dateData
    return dateMap
  }, {})

const dayDataListToMap = (dayDataList: DayDataList[]) =>
  dateListToMap(dayDataList)

const availabilityDataListToMap = (
  availabilityDataList: AvailabilityDataList[]
) => dateListToMap(availabilityDataList)

const capacityToRow = ({
  capacityData,
  rate,
  hotelId,
  roomTypeId,
  fatherId,
  defaultDayDataType
}: {
  capacityData: RoomTypeCapacityDataList
  hotelId: string
  roomTypeId: string
  fatherId: string
  rate: RoomTypeRateDataList
  defaultDayDataType?: keyof DayDataTypes
}): CapacityRow => {
  const firstChannel = capacityData.channelDataList[0]
  const rowId = createRowId({
    hotelId,
    roomTypeId,
    rateId: rate.id,
    capacityId: capacityData.id,
    channelId: firstChannel.id
  })
  const dayDataList = firstChannel.dayDataList
  return {
    id: rowId,
    fatherId,
    channelsLoaded: false,
    channelsQty: +capacityData.channelNumber,
    showChannels: false,
    type: RowType.CAPACITY,
    editable: capacityData.editable,
    inherited: firstChannel.inherited,
    inheritedDataList: firstChannel.inheritedDataList,
    dayDataType: defaultDayDataType ?? 'price',
    data: {
      capacityData,
      rateId: rate.id,
      hotelId,
      dayDataList,
      dayDataByDate: dayDataListToMap(dayDataList),
      roomTypeId,
      channelId: firstChannel.id,
      name: `${rate.name} (${capacityData.name} pax)`,
      capacityId: capacityData.id
    }
  }
}

const channelToRow = ({
  channelData,
  capacityId,
  hotelId,
  rateId,
  roomTypeId,
  fatherId,
  defaultDayDataType
}: {
  channelData: ChannelDataList
  hotelId: string
  roomTypeId: string
  rateId: string
  capacityId: string
  fatherId: string
  defaultDayDataType?: keyof DayDataTypes
}): ChannelRow => {
  const channelRowId = createRowId({
    hotelId,
    roomTypeId,
    rateId,
    capacityId,
    channelId: channelData.id
  })
  return {
    type: RowType.CHANNEL,
    id: channelRowId,
    fatherId,
    editable: channelData.editable,
    inherited: channelData.inherited,
    inheritedDataList: channelData.inheritedDataList,
    dayDataType: defaultDayDataType ?? 'price',
    data: {
      name: channelData.name,
      channelId: channelData.id,
      capacityId,
      channelData,
      hotelId,
      rateId,
      roomTypeId,
      dayDataList: channelData.dayDataList,
      dayDataByDate: dayDataListToMap(channelData.dayDataList)
    }
  }
}

const mapHotelDataListCapacityRows = ({
  hotelDataList,
  cb
}: {
  hotelDataList: HotelDataList[]
  cb: (capacityRow: CapacityRow) => void
}) => {
  hotelDataList.forEach((hotelData) => {
    hotelData.roomTypeDataList.forEach((roomType) => {
      roomType.rateDataList.forEach((rate) => {
        const rateRowCapacityQty = rate.numberCapacityList - 1
        if (rateRowCapacityQty > 0) {
          const fatherCapacity = rate.roomTypeCapacityDataList[0]
          const rateFatherchannel = fatherCapacity.channelDataList[0]
          rate.roomTypeCapacityDataList.slice(1).forEach((capacity) => {
            const capacityRow = capacityToRow({
              capacityData: capacity,
              hotelId: hotelData.id,
              roomTypeId: roomType.id,
              rate,
              fatherId: createRowId({
                hotelId: hotelData.id,
                roomTypeId: roomType.id,
                rateId: rate.id,
                capacityId: fatherCapacity.id,
                channelId: rateFatherchannel.id
              })
            })
            cb(capacityRow)
          })
        }
      })
    })
  })
}

const mapHotelDataListChannelRows = ({
  hotelDataList,
  cb
}: {
  hotelDataList: HotelDataList[]
  cb: (capacityRow: ChannelRow) => void
}) => {
  hotelDataList.forEach((hotelData) => {
    hotelData.roomTypeDataList.forEach((roomType) => {
      roomType.rateDataList.forEach((rate) => {
        rate.roomTypeCapacityDataList.forEach((capacity) => {
          if (+capacity.channelNumber < 1) return
          const capacityFatherChannel = capacity.channelDataList[0]
          capacity.channelDataList.slice(1).forEach((channel) => {
            const channelRow = channelToRow({
              channelData: channel,
              hotelId: hotelData.id,
              roomTypeId: roomType.id,
              rateId: rate.id,
              capacityId: capacity.id,
              fatherId: createRowId({
                hotelId: hotelData.id,
                roomTypeId: roomType.id,
                rateId: rate.id,
                capacityId: capacity.id,
                channelId: capacityFatherChannel.id
              })
            })
            cb(channelRow)
          })
        })
      })
    })
  })
}

const hotelDataListToRows = ({
  hotelDataList,
  loadedCapacityRows,
  loadedChannelRows,
  defaultDayDataTypes
}: {
  hotelDataList: HotelDataList[]
  loadedCapacityRows?: { [rowId: string]: CapacityRow[] }
  loadedChannelRows?: { [rowId: string]: ChannelRow[] }
  defaultDayDataTypes?: { [rowId: string]: keyof DayDataTypes }
}): RatesDataGridRow[] => {
  const newRows: RatesDataGridRow[] = []

  hotelDataList.forEach((hotelData) => {
    const hotelId = createRowId({
      hotelId: hotelData.id
    })
    newRows.push({
      id: hotelId,
      type: RowType.HOTEL,
      data: {
        name: hotelData.name
      }
    })

    // Room Type Rows
    hotelData.roomTypeDataList.forEach((roomType) => {
      const roomTypeId = createRowId({
        hotelId: hotelData.id,
        roomTypeId: roomType.id
      })
      newRows.push({
        id: roomTypeId,
        type: RowType.ROOM_TYPE,
        data: {
          availabilityDataList: roomType.availabilityDataList,
          availabilityDataByDate: availabilityDataListToMap(
            roomType.availabilityDataList
          ),
          hotelId: hotelData.id,
          name: roomType.name,
          totalRooms: roomType.totalRooms
        }
      })

      // Rate Rows
      roomType.rateDataList.forEach((rateData) => {
        const rateFirstCapacity = rateData.roomTypeCapacityDataList[0]
        const rateFirstCapacityFirstChannel =
          rateFirstCapacity.channelDataList[0]

        const rateRowChannelQty = +rateFirstCapacity.channelNumber ?? 0
        const rateRowCapacityQty = rateData.numberCapacityList - 1

        const rateDataId = createRowId({
          hotelId: hotelData.id,
          roomTypeId: roomType.id,
          rateId: rateData.id,
          capacityId: rateFirstCapacity.id,
          channelId: rateFirstCapacityFirstChannel.id
        })

        const childrenChannelRows = loadedChannelRows?.[rateDataId] ?? []
        const childrenCapacityRows = loadedCapacityRows?.[rateDataId] ?? []

        const defaultShowRateChannels = childrenChannelRows.length > 0
        const defaultShowRateCapacities = childrenCapacityRows.length > 0

        const rateRow: RateRow = {
          id: rateDataId,
          type: RowType.RATE,
          showCapacities: defaultShowRateCapacities,
          showChannels: defaultShowRateChannels,
          capacitiesQty: rateRowCapacityQty,
          channelsQty: rateRowChannelQty,
          capacitiesLoaded: defaultShowRateCapacities,
          channelsLoaded: defaultShowRateChannels,
          editable: rateFirstCapacityFirstChannel.editable,
          inherited: rateFirstCapacityFirstChannel.inherited,
          inheritedDataList: rateFirstCapacityFirstChannel.inheritedDataList,
          dayDataType: defaultDayDataTypes?.[roomType.id] ?? 'price',
          data: {
            name: `${rateData.name} (${rateFirstCapacity.name} pax)`,
            capacityId: rateFirstCapacity.id,
            dayDataList: rateFirstCapacityFirstChannel.dayDataList,
            dayDataByDate: dayDataListToMap(
              rateFirstCapacityFirstChannel.dayDataList
            ),
            channelId: rateFirstCapacityFirstChannel.id,
            hotelId: hotelData.id,
            roomTypeId: roomType.id,
            rateData,
            rateId: rateData.id
          }
        }

        newRows.push(rateRow)

        // Add Rate Channels

        if (rateRowChannelQty > 0) {
          if (childrenChannelRows.length > 0) {
            /* Add Loaded Channels if exist */
            newRows.push(...childrenChannelRows)
          }
          // else {
          //   /* otherwise generate skeletonRows */
          //   for (let index = 0; index < rateRowChannelQty; index++) {
          //     const skeletonRow: SkeletonRow = {
          //       id: generateUUID(),
          //       type: RowType.CHANNEL_SKELETON,
          //       fatherId: rateRow.id
          //     }
          //     newRows.push(skeletonRow)
          //   }
          // }
        }

        // Add Rate Capacities

        if (rateRowCapacityQty > 0) {
          if (childrenCapacityRows.length > 0) {
            /* Add Loaded Capacities */
            childrenCapacityRows.forEach((capacityRow) => {
              const loadedCapacityChannels =
                loadedChannelRows?.[capacityRow.id] ?? []

              const defaultOpenChannels = loadedCapacityChannels.length > 0

              newRows.push({
                ...capacityRow,
                showChannels: defaultOpenChannels,
                channelsLoaded: defaultOpenChannels
              })
              if (loadedCapacityChannels.length > 0) {
                /* Add loaded capacity channels */
                newRows.push(...loadedCapacityChannels)
                // return
              }
            })
          }
        }
      })
    })
  })

  return newRows
}

const getRowEntityIds = (rowId: string) => {
  const splittedRowIds = rowId.split('/')
  return {
    hotelId: splittedRowIds[0],
    roomTypeId: splittedRowIds[1],
    rateId: splittedRowIds[2],
    capacityId: splittedRowIds[3],
    channelId: splittedRowIds[4]
  }
}

const createRowId = ({
  hotelId = '',
  roomTypeId = '',
  rateId = '',
  capacityId = '',
  channelId = ''
}: {
  hotelId?: string
  roomTypeId?: string
  rateId?: string
  capacityId?: string
  channelId?: string
}): string => {
  return `${hotelId}/${roomTypeId}/${rateId}/${capacityId}/${channelId}`
}

export const RatesDataGridContextUtils = {
  hotelDataListToRows,
  mapHotelDataListCapacityRows,
  mapHotelDataListChannelRows,
  channelToRow,
  capacityToRow,
  getRowEntityIds,
  dateListToMap,
  dayDataListToMap,
  availabilityDataListToMap
}
